import { ReactNode, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import "moment/dist/locale/de";
import "moment/dist/locale/en-gb";
import "moment/dist/locale/es";
import "moment/dist/locale/fr";
import "moment/dist/locale/it";
import "moment/dist/locale/pl";
import "moment/dist/locale/hu";
import "moment/dist/locale/lt";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { ApiResources } from "@/api/resources";
import useLanguageConverter from "@/shared/hooks/useLanguageConverter";
import useResponse from "@/shared/hooks/useResponse";
import { useUserDataStore } from "@/stores/useUserDataStore";

import { DATE_FORMATS, LANGUAGES } from "../../shared";
import {
  getStoredLanguage,
  getValidLanguage
} from "../../shared/functions/functions";
import { LANGUAGES_ARRAY } from "./constants";
import LanguageContext, { LanguageInitialState } from "./LanguageContext";

interface LanguageStateProps {
  children: ReactNode;
}

const LanguageProvider = ({ children }: LanguageStateProps) => {
  const user = useUserDataStore(state => state.userData);

  const { patchData } = useAxiosPrivate();
  const { i18n, t } = useTranslation();
  const { convertFromLocale, handleLanguageChange, setMomentLocale } =
    useLanguageConverter();
  const { handleCommonError } = useResponse();

  const getLanguage = () => {
    return getValidLanguage(getStoredLanguage(), i18n.language as LANGUAGES);
  };

  const getFormatterData = () => {
    const formatter = new Intl.DateTimeFormat(undefined, {
      day: "numeric",
      month: "numeric",
      year: "numeric"
    });

    return formatter.formatToParts(new Date());
  };

  const getUserBrowserDateFormat = () => {
    const formattedParts = getFormatterData();

    return formattedParts
      .map(part => {
        switch (part.type) {
          case "day":
            return DATE_FORMATS.Day;
          case "month":
            return DATE_FORMATS.Month;
          case "year":
            return DATE_FORMATS.Year;
          default:
            return "-";
        }
      })
      .join("");
  };

  const getDateFormat = () => {
    const dateFormatFromApi = user?.timeSettings?.dateFormat;

    if (!dateFormatFromApi) {
      return getUserBrowserDateFormat();
    }

    return dateFormatFromApi;
  };

  const state: LanguageInitialState = {
    languages: LANGUAGES_ARRAY,
    language: getLanguage(),
    userBrowserDateFormat: getUserBrowserDateFormat(),
    currentDateFormat: getDateFormat()
  };

  useEffect(() => {
    setMomentLocale(state.language, state.languages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setNewLanguage = async (lang: LANGUAGES) => {
    const convertedLanguage = convertFromLocale(lang);

    try {
      await patchData(ApiResources.UsersMe, {
        language: convertedLanguage
      });
      handleLanguageChange(lang, state.languages);
    } catch (error) {
      handleCommonError(error, t);
    }
  };

  const languageContextValue = useMemo(
    () => ({
      languages: state.languages,
      language: state.language,
      userBrowserDateFormat: state.userBrowserDateFormat,
      currentDateFormat: state.currentDateFormat,
      setNewLanguage
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.language, state.languages, user]
  );

  return (
    <LanguageContext.Provider value={languageContextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
